/**
 * THE SELECTED PLANNING SCOPE (`design-docs/planning-scopes.md` §5, PS-D12).
 *
 * A scope is a full-network planning context with its own configuration, policy, schedule and
 * runs. The scopes come from `bootstrap.planningScopes` (default first, then by name) and the
 * selection is URL state: `?scopeId=`. THE RULES:
 *
 *   - An absent id, or one not in the list, is the DEFAULT scope. A stale id (a bookmark to a
 *     deleted scope) is replaced in the URL, never treated as an error.
 *   - `requestParams` carries `planningScopeId` ONLY when the selection is not the default, so a
 *     single-scope tenant's requests (KCC) are byte-for-byte what they were.
 *   - `scopeId` is the RESOLVED id, and goes into every scope-keyed query key (`scopedParams`).
 *   - `multiScope` gates all scope UI: with one scope there is no switcher and no scope name.
 *
 * A bootstrap that carries no list (an older server, a test fixture) resolves to no scope:
 * `scopeId` null, default, `multiScope` false, no request parameter.
 *
 * The URL is read through `useOptionalUrlValue`, so this hook (and everything built on it, such
 * as `useCurrentPlan`) also works under a `QueryClientProvider` with no router.
 */
import { useEffect, useMemo } from "react";

import type { BootstrapPlanningScope } from "../client";
import { useOptionalUrlValue } from "../../routes/urlState";
import { useBootstrap } from "./useBootstrap";

export interface PlanningScopeSelection {
  /** Every scope, default first. Empty before the bootstrap answers. */
  scopes: readonly BootstrapPlanningScope[];
  /** The resolved selection; null only when there are no scopes to select from. */
  selected: BootstrapPlanningScope | null;
  /** The resolved scope's id, for query keys. Null when there is no list. */
  scopeId: string | null;
  /** The selection is the default scope (or there is none). */
  isDefault: boolean;
  /** More than one scope exists: the only time any scope UI shows. */
  multiScope: boolean;
  /** Spread into a scope-keyed request's `query`. Empty for the default scope. */
  requestParams: { planningScopeId?: string };
  /** The bootstrap has answered, so `selected` is final. */
  settled: boolean;
}

const NO_SCOPES: readonly BootstrapPlanningScope[] = [];
const NO_PARAMS: { planningScopeId?: string } = {};

export function usePlanningScope(): PlanningScopeSelection {
  const { data: boot } = useBootstrap();
  const { value: urlScopeId, drop } = useOptionalUrlValue("scopeId");
  const scopes = boot?.planningScopes ?? NO_SCOPES;
  const settled = boot !== undefined;

  const found = urlScopeId ? scopes.find((s) => s.id === urlScopeId) : undefined;
  /* Stale: the URL names a scope the list does not hold. Replace it, once the list is known. */
  const stale = settled && scopes.length > 0 && urlScopeId !== undefined && found === undefined;
  useEffect(() => {
    if (stale) drop();
  }, [stale, drop]);

  return useMemo(() => {
    const defaultScope = scopes.find((s) => s.isDefault) ?? scopes[0] ?? null;
    const selected = found ?? defaultScope;
    const isDefault = selected === null || selected.id === defaultScope?.id;
    return {
      scopes,
      selected,
      scopeId: selected?.id ?? null,
      isDefault,
      multiScope: scopes.length > 1,
      requestParams: isDefault || selected === null ? NO_PARAMS : { planningScopeId: selected.id },
      settled,
    };
  }, [scopes, found, settled]);
}

/** The two things a scope-keyed read needs: its key id and its request parameter. */
export interface ScopeReadParams {
  scopeId: string | null;
  requestParams: { planningScopeId?: string };
}

/**
 * The scope a read should use: `override` (an explicit planning scope id, such as the one a
 * scenario belongs to) when given, otherwise the selected scope. An override carries
 * `planningScopeId` unless it is the default scope, exactly as `requestParams` does.
 */
export function useScopeReadParams(override?: string): ScopeReadParams {
  const selection = usePlanningScope();
  return useMemo(() => {
    if (override === undefined || override === selection.scopeId) {
      return { scopeId: selection.scopeId, requestParams: selection.requestParams };
    }
    const defaultScope = selection.scopes.find((s) => s.isDefault) ?? selection.scopes[0];
    return {
      scopeId: override,
      requestParams: override === defaultScope?.id ? NO_PARAMS : { planningScopeId: override },
    };
  }, [override, selection]);
}
